import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { authApi, setAccessToken, getAccessToken } from "@/lib/api-client";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Auth is driven by an httpOnly `uc_session` cookie set at login/register.
  // We ask /auth/me — if the cookie (or in-memory header) is valid we're in;
  // otherwise the user is anonymous.
  const refresh = useCallback(async () => {
    try {
      const me = await authApi.me();
      setUser(me);
    } catch (e) {
      // 401 is the expected anonymous path — no console noise.
      if (e?.response?.status !== 401) console.error("auth refresh failed", e);
      setAccessToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password) => {
    const r = await authApi.login(email, password);
    setAccessToken(r.token);
    setUser(r.user);
    return r.user;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const r = await authApi.register(email, password, name);
    setAccessToken(r.token);
    setUser(r.user);
    return r; // includes connect_onboarding_url when Public Rollout is ON
  }, []);

  const logout = useCallback(async () => {
    try { await authApi.logout(); } catch (e) { console.error("logout failed", e); }
    setAccessToken(null);
    setUser(null);
  }, []);

  const patchUser = useCallback((patch) => {
    setUser((u) => (u ? { ...u, ...patch } : u));
  }, []);

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, refresh, patchUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
// Re-export so callers importing from auth.jsx keep working.
export { getAccessToken };
